import React from 'react';
import BlurSkeleton from './BlurSkeleton';
import SkeletonRegion from './SkeletonRegion';
import useStyles from './TabSkeletons.styles';

/**
 * One skeleton per tab. Each mirrors the layout of the content it stands in
 * for, so switching tabs never falls back to a generic loading state.
 */

interface IShellProps {
  children: React.ReactNode;
}

const TabShell: React.FC<IShellProps> = ({ children }) => {
  const classes = useStyles();
  return <SkeletonRegion className={classes.tab}>{children}</SkeletonRegion>;
};

interface ITableProps {
  columns: number;
  rows: number;
}

export const TableSkeleton: React.FC<ITableProps> = ({ columns, rows }) => {
  const classes = useStyles();
  const template = `repeat(${columns}, minmax(0, 1fr))`;
  return (
    <div className={classes.table}>
      <div className={classes.tableHead} style={{ gridTemplateColumns: template }}>
        {Array.from({ length: columns }, (_, col) => (
          <BlurSkeleton key={col} height={11} width="70%" delay={col * 60} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className={classes.tableRow}
          style={{ gridTemplateColumns: template }}
        >
          {Array.from({ length: columns }, (_, col) => (
            <BlurSkeleton key={col} height={13} subtle delay={row * 90 + col * 40} />
          ))}
        </div>
      ))}
    </div>
  );
};

const CardsSkeleton: React.FC<{ count: number }> = ({ count }) => {
  const classes = useStyles();
  return (
    <div className={classes.cardGrid}>
      {Array.from({ length: count }, (_, index) => (
        <div className={classes.card} key={index}>
          <BlurSkeleton height={11} width="55%" delay={index * 110} />
          <BlurSkeleton height={30} width="40%" delay={index * 110 + 60} />
          <BlurSkeleton height={10} width="85%" delay={index * 110 + 120} />
        </div>
      ))}
    </div>
  );
};

const CalendarSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.card}>
      <div className={classes.row}>
        <BlurSkeleton height={20} width={150} />
        <BlurSkeleton variant="circle" height={28} />
        <BlurSkeleton variant="circle" height={28} />
      </div>
      <div className={classes.calendarGrid}>
        {Array.from({ length: 7 }, (_, index) => (
          <BlurSkeleton key={`dow-${index}`} height={10} delay={index * 40} />
        ))}
        {Array.from({ length: 35 }, (_, index) => (
          <BlurSkeleton key={index} variant="rect" height={38} radius={8} subtle delay={index * 25} />
        ))}
      </div>
    </div>
  );
};

/* ── Admin dashboard tabs ─────────────────────────────────────────────── */

/** Create-reservation form on top of the reservation history table. */
export const ReservationsTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.card}>
        <BlurSkeleton height={18} width={220} />
        <div className={classes.row}>
          <BlurSkeleton variant="rect" height={44} radius={10} className={classes.grow} delay={80} />
          <BlurSkeleton variant="rect" height={44} radius={10} className={classes.grow} delay={140} />
          <BlurSkeleton variant="rect" height={44} width={150} radius={10} delay={200} />
        </div>
      </div>
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton variant="rect" height={40} width={180} radius={10} />
          <BlurSkeleton variant="rect" height={40} width={180} radius={10} delay={80} />
          <BlurSkeleton variant="rect" height={40} width={140} radius={10} delay={160} />
        </div>
        <TableSkeleton columns={6} rows={7} />
      </div>
    </TabShell>
  );
};

/** Calendar next to its day-detail panel, shared by the admin calendar pages. */
export const CalendarDetailSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.splitGrid}>
      <CalendarSkeleton />
      <div className={classes.card}>
        <BlurSkeleton height={18} width="60%" />
        <BlurSkeleton height={10} width="80%" delay={60} />
        <BlurSkeleton variant="rect" height={12} radius={6} delay={120} />
        <BlurSkeleton variant="rect" height={12} radius={6} delay={180} />
        <BlurSkeleton variant="rect" height={12} radius={6} delay={240} />
        {Array.from({ length: 5 }, (_, index) => (
          <div className={classes.row} key={index}>
            <BlurSkeleton variant="circle" height={32} delay={index * 90} />
            <BlurSkeleton height={12} className={classes.grow} delay={index * 90 + 40} />
          </div>
        ))}
      </div>
    </div>
  );
};

/** The user table inside the users tab, where the filters already render. */
export const UsersListSkeleton: React.FC = () => <TableSkeleton columns={5} rows={8} />;

/** The asset categories card on its own, without the bulk import sibling. */
export const AssetCategoriesSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.card}>
      <BlurSkeleton height={18} width={220} />
      <TableSkeleton columns={3} rows={6} />
    </div>
  );
};

/** Calendar plus the day detail panel used by the lunch admin tab. */
export const LunchTabSkeleton: React.FC = () => (
  <TabShell>
    <CalendarDetailSkeleton />
  </TabShell>
);

/** Day planning controls followed by the floor map and its side panel. */
export const PlanningTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <CalendarDetailSkeleton />
      <BlurSkeleton variant="rect" height={64} radius={12} />
      <div className={classes.row}>
        <BlurSkeleton variant="rect" height={34} width={140} radius={999} />
        <BlurSkeleton variant="rect" height={34} width={110} radius={999} delay={70} />
        <BlurSkeleton variant="rect" height={34} width={110} radius={999} delay={140} />
        <BlurSkeleton variant="rect" height={34} width={130} radius={999} delay={210} />
      </div>
      <div className={classes.mapGrid}>
        <BlurSkeleton variant="rect" height={420} radius={14} />
        <div className={classes.card}>
          <BlurSkeleton height={16} width="70%" />
          {Array.from({ length: 6 }, (_, index) => (
            <BlurSkeleton key={index} height={12} delay={index * 90} />
          ))}
        </div>
      </div>
    </TabShell>
  );
};

/** User management: search bar over a wide user table. */
export const UsersTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton variant="rect" height={42} radius={10} className={classes.grow} />
          <BlurSkeleton variant="rect" height={42} width={170} radius={10} delay={90} />
        </div>
        <UsersListSkeleton />
      </div>
    </TabShell>
  );
};

/** Bulk import card stacked over the asset categories manager. */
export const AssetsTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.card}>
        <BlurSkeleton height={18} width={260} />
        <BlurSkeleton height={10} width="70%" delay={60} />
        <div className={classes.row}>
          <BlurSkeleton variant="rect" height={110} radius={12} className={classes.grow} delay={120} />
        </div>
        <BlurSkeleton variant="rect" height={42} width={180} radius={10} delay={200} />
      </div>
      <AssetCategoriesSkeleton />
    </TabShell>
  );
};

/* ── Reports tabs ─────────────────────────────────────────────────────── */

/** Lunch report: legend, four KPI cards, then the monthly breakdown tables. */
export const ReportsLunchTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <BlurSkeleton variant="rect" height={54} radius={12} />
      <CardsSkeleton count={4} />
      <div className={classes.card}>
        <BlurSkeleton height={18} width="45%" />
        <BlurSkeleton height={10} width="65%" delay={60} />
        <TableSkeleton columns={5} rows={6} />
      </div>
      <div className={classes.card}>
        <BlurSkeleton height={18} width="38%" />
        <TableSkeleton columns={4} rows={5} />
      </div>
    </TabShell>
  );
};

/** Deskbooking report: KPI cards plus attendance and no-show panels. */
export const ReportsDeskbookingTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <CardsSkeleton count={4} />
      <div className={classes.card}>
        <BlurSkeleton height={18} width="40%" />
        <BlurSkeleton variant="rect" height={240} radius={12} delay={80} />
      </div>
      <div className={classes.card}>
        <BlurSkeleton height={18} width="35%" />
        <TableSkeleton columns={4} rows={6} />
      </div>
    </TabShell>
  );
};

/** Asset report: filter chips, a chart block and the breakdown table. */
export const ReportsAssetsTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.row}>
        <BlurSkeleton variant="rect" height={36} width={130} radius={999} />
        <BlurSkeleton variant="rect" height={36} width={150} radius={999} delay={70} />
        <BlurSkeleton variant="rect" height={36} width={120} radius={999} delay={140} />
      </div>
      <CardsSkeleton count={3} />
      <div className={classes.card}>
        <BlurSkeleton height={18} width="42%" />
        <BlurSkeleton variant="rect" height={220} radius={12} delay={80} />
      </div>
      <div className={classes.card}>
        <TableSkeleton columns={5} rows={6} />
      </div>
    </TabShell>
  );
};

/* ── Talent tabs ──────────────────────────────────────────────────────── */

/** Filter row shared by the Skill Matrix tabs: a wide search plus selects. */
const TalentFiltersSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <BlurSkeleton variant="rect" height={38} width={240} radius={8} />
      {[0, 1, 2, 3, 4].map((index) => (
        <BlurSkeleton
          key={index}
          variant="rect"
          height={38}
          width={120 + ((index * 29) % 40)}
          radius={8}
          delay={(index + 1) * 60}
        />
      ))}
    </div>
  );
};

/**
 * Content-only skeletons. Use these where the page already renders its own
 * header and filters, so the placeholder does not duplicate what is on screen.
 */

/** The people table inside the directory panel. */
export const TalentDirectoryListSkeleton: React.FC = () => (
  <TableSkeleton columns={9} rows={8} />
);

/** The per-skill coverage table. */
export const TalentCoverageListSkeleton: React.FC = () => (
  <TableSkeleton columns={8} rows={8} />
);

/** The list of roles inside the roles panel. */
export const TalentRolesListSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.table}>
      {Array.from({ length: 6 }, (_, index) => (
        <div className={classes.row} key={index}>
          <BlurSkeleton height={13} width={220} delay={index * 90} />
          <BlurSkeleton variant="rect" height={24} width={80} radius={999} delay={index * 90 + 50} />
        </div>
      ))}
    </div>
  );
};

/** The skill grid: categories, each with a row of selectable chips. */
export const TalentSkillGridSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.tab}>
      {Array.from({ length: 4 }, (_, section) => (
        <div key={section}>
          <BlurSkeleton height={13} width="28%" delay={section * 120} />
          <div className={classes.chipRow} style={{ marginTop: 10 }}>
            {Array.from({ length: 7 }, (_, chip) => (
              <BlurSkeleton
                key={chip}
                variant="rect"
                height={28}
                width={70 + ((chip * 23) % 60)}
                radius={999}
                subtle
                delay={section * 120 + chip * 45}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/** People directory: filter row over the results table. */
export const TalentDirectoryTabSkeleton: React.FC = () => (
  <TabShell>
    <TalentFiltersSkeleton />
    <TableSkeleton columns={9} rows={8} />
  </TabShell>
);

/** Coverage report: filter row over the per-skill table. */
export const TalentCoverageTabSkeleton: React.FC = () => (
  <TabShell>
    <TalentFiltersSkeleton />
    <TableSkeleton columns={8} rows={8} />
  </TabShell>
);

/** Role manager: the roles panel over the skill grid. */
export const TalentRolesTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.card}>
        <BlurSkeleton height={15} width="30%" />
        <TalentRolesListSkeleton />
      </div>
      <div className={classes.card}>
        <TalentSkillGridSkeleton />
      </div>
    </TabShell>
  );
};

/** Team directory: filter row over the team member cards. */
export const TalentTeamTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <TalentFiltersSkeleton />
      <div className={classes.peopleGrid}>
        {Array.from({ length: 6 }, (_, index) => (
          <div className={classes.card} key={index}>
            <div className={classes.row}>
              <BlurSkeleton variant="circle" height={40} delay={index * 90} />
              <div className={classes.grow}>
                <BlurSkeleton height={13} width="70%" delay={index * 90 + 40} />
                <BlurSkeleton height={10} width="45%" delay={index * 90 + 80} />
              </div>
            </div>
            <div className={classes.chipRow}>
              <BlurSkeleton variant="rect" height={22} width={72} radius={999} delay={index * 90} />
              <BlurSkeleton variant="rect" height={22} width={90} radius={999} delay={index * 90 + 50} />
            </div>
            <BlurSkeleton height={10} width="90%" delay={index * 90 + 120} />
            <BlurSkeleton height={10} width="60%" delay={index * 90 + 160} />
          </div>
        ))}
      </div>
    </TabShell>
  );
};

/** Personal skill profile: identity header then the skill sections. */
export const TalentProfileTabSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <TabShell>
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton variant="circle" height={72} />
          <div className={classes.grow}>
            <BlurSkeleton height={20} width="40%" delay={60} />
            <BlurSkeleton height={12} width="28%" delay={120} />
          </div>
        </div>
      </div>
      {Array.from({ length: 3 }, (_, section) => (
        <div className={classes.card} key={section}>
          <BlurSkeleton height={15} width="30%" delay={section * 130} />
          {Array.from({ length: 4 }, (_, row) => (
            <div className={classes.row} key={row}>
              <BlurSkeleton height={12} width={200} delay={section * 130 + row * 70} />
              <BlurSkeleton
                variant="rect"
                height={10}
                radius={999}
                className={classes.grow}
                delay={section * 130 + row * 70 + 40}
              />
            </div>
          ))}
        </div>
      ))}
    </TabShell>
  );
};
